import { useSearchParams } from "react-router-dom";

export default function usePersistentURL() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values directly from searchParams to stay in sync with URL
  const currentPage = Number(searchParams?.get("page")) || 1;
  const searchQuery = searchParams?.get("q") || "";
  const pageSize = Number(searchParams?.get("pageSize")) || 10;
  const statusFilter = searchParams?.get("status") || "";
  const sortBy = searchParams?.get("sort") || "";
  // Absent means "my own branch" — the server resolves that from the session,
  // so an empty value here is a real state, not a missing one.
  const branchFilter = searchParams?.get("branch") || "";

  const onPageChange = (newValue: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", newValue.toString());
      return newParams;
    });
  };

  const onSearchQueryChange = (newValue: string, resetPage = true) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (newValue) {
        newParams.set("q", newValue);
      } else {
        newParams.delete("q");
      }
      // Reset page to 1 when search query changes to avoid race condition
      if (resetPage) {
        newParams.set("page", "1");
      }
      return newParams;
    });
  };

  const onPageSizeChange = (newValue: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("pageSize", newValue.toString());
      return newParams;
    });
  };

  const onStatusFilterChange = (newValue: string, resetPage = true) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (newValue) {
        newParams.set("status", newValue);
      } else {
        newParams.delete("status");
      }
      // Reset page to 1 when status filter changes to avoid race condition
      if (resetPage) {
        newParams.set("page", "1");
      }
      return newParams;
    });
  };

  const onSortByChange = (newValue: string, resetPage = true) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (newValue) {
        newParams.set("sort", newValue);
      } else {
        newParams.delete("sort");
      }
      // Reset page to 1 when sort changes to avoid race condition
      if (resetPage) {
        newParams.set("page", "1");
      }
      return newParams;
    });
  };

  const onBranchFilterChange = (newValue: string, resetPage = true) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (newValue) {
        newParams.set("branch", newValue);
      } else {
        newParams.delete("branch");
      }
      // Reset page to 1 when branch changes to avoid race condition
      if (resetPage) {
        newParams.set("page", "1");
      }
      return newParams;
    });
  };

  return {
    searchQuery,
    currentPage,
    pageSize,
    statusFilter,
    sortBy,
    branchFilter,
    onPageChange,
    onSearchQueryChange,
    onPageSizeChange,
    onStatusFilterChange,
    onSortByChange,
    onBranchFilterChange,
  };
}
